import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Phone, MapPin, CheckCircle2, Send, AlertCircle, Loader2 } from 'lucide-react';
import { Navbar } from '../../components/common/Navbar';
import { Footer } from '../../components/common/Footer';
import { Button } from '../../components/common/Button';
import { Badge } from '../../components/common/Badge';
import { COMPANY_INFO } from '../../data/siteData';
import { SEO } from '../../components/common/SEO';
import { PRIVACY_VERSION } from '../../contact/schema';

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: string | HTMLElement,
        options: {
          sitekey: string;
          callback?: (token: string) => void;
          'error-callback'?: (errorCode: string) => void;
          'expired-callback'?: () => void;
          theme?: 'light' | 'dark' | 'auto';
        }
      ) => string;
      reset: (widgetId?: string) => void;
      remove: (widgetId?: string) => void;
    };
  }
}

const TURNSTILE_SITE_KEY =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_TURNSTILE_SITE_KEY) ||
  '0x4AAAAAAFvBWcyV17LkO9qa';

const INQUIRY_TYPES = [
  {
    id: 'facility',
    label: '自社施設の電力・設備について（電気代削減・設備設計・施工など）',
  },
  {
    id: '1minute-solar',
    label: '1minute solar・太陽光の正式検討・個別試算について',
  },
  {
    id: 'spaq-core',
    label: 'SPAQ CORE（太陽光・空調・蓄電池の協調制御）について',
  },
  {
    id: 'partner',
    label: '協業・販売・施工連携・共同検討について',
  },
  {
    id: 'other',
    label: 'その他のお問い合わせ（イルミネーション事業・取材等）',
  },
];

export default function ContactPage() {
  const [searchParams] = useSearchParams();
  const initialTypeParam = searchParams.get('type');

  // Form State
  const [selectedType, setSelectedType] = useState<string>('facility');
  const [organization, setOrganization] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [facilityInfo, setFacilityInfo] = useState('');
  const [message, setMessage] = useState('');
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [website, setWebsite] = useState(''); // Honeypot field
  const [turnstileToken, setTurnstileToken] = useState<string>('');

  // Submission State
  const [idempotencyKey, setIdempotencyKey] = useState<string>(() => crypto.randomUUID());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submittedData, setSubmittedData] = useState<{
    receiptId: string;
    receivedAt: string;
  } | null>(null);

  // Turnstile container ref & widget id
  const turnstileContainerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (
      initialTypeParam &&
      INQUIRY_TYPES.some((t) => t.id === initialTypeParam)
    ) {
      setSelectedType(initialTypeParam);
    }
  }, [initialTypeParam]);

  // Load Turnstile Script
  useEffect(() => {
    if (submittedData) return;

    let isMounted = true;

    const renderWidget = () => {
      if (!isMounted || !turnstileContainerRef.current || !window.turnstile) return;
      if (widgetIdRef.current) return;

      try {
        const id = window.turnstile.render(turnstileContainerRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          theme: 'light',
          callback: (token: string) => {
            if (isMounted) {
              setTurnstileToken(token);
              setErrorMessage(null);
            }
          },
          'expired-callback': () => {
            if (isMounted) setTurnstileToken('');
          },
          'error-callback': () => {
            if (isMounted) {
              setTurnstileToken('');
              setErrorMessage('セキュリティ認証の読み込みに失敗しました。ページを再読み込みしてください。');
            }
          },
        });
        widgetIdRef.current = id;
      } catch (err) {
        console.warn('Turnstile render warning:', err);
      }
    };

    if (window.turnstile) {
      renderWidget();
    } else {
      const existingScript = document.querySelector('script[src*="turnstile/v0/api.js"]');
      if (!existingScript) {
        const script = document.createElement('script');
        script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
        script.async = true;
        script.defer = true;
        script.onload = () => renderWidget();
        document.head.appendChild(script);
      } else {
        const checkInterval = setInterval(() => {
          if (window.turnstile) {
            clearInterval(checkInterval);
            renderWidget();
          }
        }, 100);
        return () => clearInterval(checkInterval);
      }
    }

    return () => {
      isMounted = false;
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          // ignore
        }
        widgetIdRef.current = null;
      }
    };
  }, [submittedData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);
    setFieldErrors({});

    // Client-side quick validation
    const errors: Record<string, string> = {};
    if (!organization.trim()) errors.organization = '貴社名・団体名を入力してください。';
    if (!name.trim()) errors.name = 'ご担当者名を入力してください。';
    if (!email.trim()) errors.email = 'メールアドレスを入力してください。';
    if (!message.trim()) errors.message = 'お問い合わせ内容を入力してください。';
    if (!privacyAccepted) errors.privacyAccepted = 'プライバシーポリシーへの同意が必要です。';
    if (!turnstileToken) {
      errors.turnstile = 'セキュリティ認証（Turnstile）が完了していません。チェックを行ってください。';
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setErrorMessage('入力内容に不足または不備があります。各項目をご確認ください。');
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        schemaVersion: 1,
        category: selectedType,
        organization: organization.trim(),
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim() ? phone.trim() : null,
        facilityInfo: facilityInfo.trim() ? facilityInfo.trim() : null,
        message: message.trim(),
        privacyAccepted: true,
        privacyVersion: PRIVACY_VERSION,
        turnstileToken,
        website, // honeypot
      };

      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Idempotency-Key': idempotencyKey,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json().catch(() => null);

      if (response.ok && data?.status === 'accepted') {
        setSubmittedData({
          receiptId: data.receiptId,
          receivedAt: data.receivedAt,
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        // Handle server errors
        if (data?.fieldErrors) {
          setFieldErrors(data.fieldErrors);
        }
        setErrorMessage(
          data?.message ||
          'お問い合わせの送信に失敗しました。恐れ入りますが入力内容をご確認の上、再度お試しください。'
        );
        // Reset turnstile token if verification failed
        if (response.status === 403 || response.status === 422) {
          setTurnstileToken('');
          if (widgetIdRef.current && window.turnstile) {
            window.turnstile.reset(widgetIdRef.current);
          }
        }
      }
    } catch (err) {
      console.error('Contact submission network error:', err);
      setErrorMessage(
        '通信エラーが発生しました。インターネット接続をご確認の上、もう一度お試しください。'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatJstString = (isoString?: string) => {
    if (!isoString) return '';
    try {
      const date = new Date(isoString);
      return new Intl.DateTimeFormat('ja-JP', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'Asia/Tokyo',
      }).format(date);
    } catch {
      return isoString;
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white text-navy">
      <SEO
        title="お問い合わせ・ご相談｜Spaq株式会社"
        description="自家消費型太陽光の設計・シミュレーション、SPAQ COREによる現場電力制御、協業など、電力に関する各種ご相談・お問い合わせを受け付けています。"
        canonicalPath="/contact"
      />
      <Navbar />

      <main className="flex-1">
        {/* Hero */}
        <section className="bg-hero-gradient border-b border-surface-border py-12 md:py-16">
          <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
            <Badge variant="cyan" className="mb-4">CONTACT US</Badge>
            <h1 className="text-3xl sm:text-4xl font-bold text-navy tracking-tight">
              お問い合わせ・ご相談
            </h1>
            <p className="mt-3 text-sm sm:text-base text-navy-muted max-w-2xl leading-relaxed">
              施設の電力課題のご相談、「1minute solar」による太陽光の試算・正式設計、「SPAQ CORE」の導入、およびパートナー協業について、お気軽にお問い合わせください。
            </p>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-white">
          <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
              {/* Left 8 cols: Form or Completion message */}
              <div className="lg:col-span-8">
                {submittedData ? (
                  <div className="rounded-3xl border border-spaq-cyan/40 bg-surface-sky p-8 sm:p-12 text-center space-y-6">
                    <div className="w-16 h-16 rounded-full bg-spaq-cyan text-white flex items-center justify-center mx-auto shadow-md">
                      <CheckCircle2 className="w-9 h-9" />
                    </div>
                    <div className="space-y-2">
                      <h2 className="text-2xl sm:text-3xl font-bold text-navy">
                        お問い合わせを受け付けました
                      </h2>
                      <p className="text-sm sm:text-base text-navy-muted max-w-xl mx-auto leading-relaxed">
                        ご連絡いただきありがとうございます。内容を確認の上、担当者より通常2〜3営業日以内に折り返しご連絡いたします。
                      </p>
                    </div>

                    {/* Receipt Details Box */}
                    <div className="max-w-md mx-auto bg-white rounded-2xl border border-surface-border p-6 text-left shadow-soft space-y-3">
                      <div className="text-xs font-bold text-spaq-cyan-dark uppercase tracking-wider">
                        受付番号（お控え）
                      </div>
                      <div className="text-2xl font-bold font-mono text-navy tracking-wide bg-surface-slate/60 px-4 py-2.5 rounded-xl border border-surface-border select-all text-center">
                        {submittedData.receiptId}
                      </div>
                      <div className="text-xs text-navy-muted flex justify-between pt-1">
                        <span>受付日時</span>
                        <span className="font-medium text-navy">{formatJstString(submittedData.receivedAt)}</span>
                      </div>
                      <p className="text-[11px] text-navy-muted leading-relaxed pt-2 border-t border-surface-border">
                        ※ お問い合わせ内容の確認やお電話でのご連絡の際は、上記の受付番号をお伝えいただくとスムーズです。
                      </p>
                    </div>

                    <div className="pt-4 flex flex-col sm:flex-row gap-4 justify-center">
                      <Button to="/" variant="navy">
                        トップページへ戻る
                      </Button>
                      <button
                        type="button"
                        onClick={() => {
                          setSubmittedData(null);
                          setOrganization('');
                          setName('');
                          setEmail('');
                          setPhone('');
                          setFacilityInfo('');
                          setMessage('');
                          setPrivacyAccepted(false);
                          setTurnstileToken('');
                          setIdempotencyKey(crypto.randomUUID());
                        }}
                        className="inline-flex items-center justify-center rounded-xl px-5 py-2.5 text-sm font-semibold border border-surface-border bg-white text-navy hover:bg-surface-slate transition-colors"
                      >
                        別のお問い合わせを送信する
                      </button>
                    </div>
                  </div>
                ) : (
                  <form
                    onSubmit={handleSubmit}
                    noValidate
                    className="rounded-3xl border border-surface-border bg-surface-slate/50 p-6 sm:p-9 shadow-soft space-y-6"
                  >
                    {/* Overall Error Message */}
                    {errorMessage && (
                      <div className="rounded-2xl border border-rose-300 bg-rose-50 p-4 text-rose-800 flex items-start gap-3 text-sm">
                        <AlertCircle className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" />
                        <div>
                          <div className="font-bold">送信を完了できませんでした</div>
                          <div className="text-xs sm:text-sm mt-0.5">{errorMessage}</div>
                        </div>
                      </div>
                    )}

                    {/* Honeypot field (hidden from users) */}
                    <div className="hidden" aria-hidden="true" style={{ display: 'none' }}>
                      <label htmlFor="website-field">WebサイトURL（入力不要）</label>
                      <input
                        id="website-field"
                        type="text"
                        name="website"
                        value={website}
                        onChange={(e) => setWebsite(e.target.value)}
                        tabIndex={-1}
                        autoComplete="off"
                      />
                    </div>

                    {/* 相談種別 */}
                    <div>
                      <label className="block text-sm font-bold text-navy mb-3">
                        ご相談種別 <span className="text-rose-500 text-xs">※必須</span>
                      </label>
                      <div className="space-y-2.5">
                        {INQUIRY_TYPES.map((t) => (
                          <label
                            key={t.id}
                            className={`flex items-start gap-3 p-3.5 rounded-2xl border cursor-pointer transition-all ${
                              selectedType === t.id
                                ? 'bg-surface-sky border-spaq-cyan text-navy font-semibold shadow-sm'
                                : 'bg-white border-surface-border text-navy-muted hover:border-spaq-cyan/40'
                            }`}
                          >
                            <input
                              type="radio"
                              name="inquiryType"
                              value={t.id}
                              checked={selectedType === t.id}
                              onChange={() => setSelectedType(t.id)}
                              className="mt-1 accent-spaq-cyan"
                            />
                            <span className="text-xs sm:text-sm">{t.label}</span>
                          </label>
                        ))}
                      </div>
                      {fieldErrors.category && (
                        <p className="mt-1.5 text-xs text-rose-600">{fieldErrors.category}</p>
                      )}
                    </div>

                    {/* 会社名・お名前 */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-xs font-bold text-navy mb-1.5">
                          貴社名・団体名 <span className="text-rose-500">※必須</span>
                        </label>
                        <input
                          type="text"
                          required
                          value={organization}
                          onChange={(e) => {
                            setOrganization(e.target.value);
                            if (fieldErrors.organization) {
                              setFieldErrors((prev) => ({ ...prev, organization: '' }));
                            }
                          }}
                          placeholder="例：株式会社〇〇"
                          className={`w-full rounded-xl border bg-white px-4 py-3 text-sm text-navy focus:outline-none ${
                            fieldErrors.organization
                              ? 'border-rose-400 focus:border-rose-500 bg-rose-50/20'
                              : 'border-surface-border focus:border-spaq-cyan'
                          }`}
                        />
                        {fieldErrors.organization && (
                          <p className="mt-1 text-xs text-rose-600">{fieldErrors.organization}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-navy mb-1.5">
                          ご担当者名 <span className="text-rose-500">※必須</span>
                        </label>
                        <input
                          type="text"
                          required
                          value={name}
                          onChange={(e) => {
                            setName(e.target.value);
                            if (fieldErrors.name) {
                              setFieldErrors((prev) => ({ ...prev, name: '' }));
                            }
                          }}
                          placeholder="例：山田 太郎"
                          className={`w-full rounded-xl border bg-white px-4 py-3 text-sm text-navy focus:outline-none ${
                            fieldErrors.name
                              ? 'border-rose-400 focus:border-rose-500 bg-rose-50/20'
                              : 'border-surface-border focus:border-spaq-cyan'
                          }`}
                        />
                        {fieldErrors.name && (
                          <p className="mt-1 text-xs text-rose-600">{fieldErrors.name}</p>
                        )}
                      </div>
                    </div>

                    {/* メール・電話 */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-xs font-bold text-navy mb-1.5">
                          メールアドレス <span className="text-rose-500">※必須</span>
                        </label>
                        <input
                          type="email"
                          required
                          value={email}
                          onChange={(e) => {
                            setEmail(e.target.value);
                            if (fieldErrors.email) {
                              setFieldErrors((prev) => ({ ...prev, email: '' }));
                            }
                          }}
                          placeholder="example@company.co.jp"
                          className={`w-full rounded-xl border bg-white px-4 py-3 text-sm text-navy focus:outline-none ${
                            fieldErrors.email
                              ? 'border-rose-400 focus:border-rose-500 bg-rose-50/20'
                              : 'border-surface-border focus:border-spaq-cyan'
                          }`}
                        />
                        {fieldErrors.email && (
                          <p className="mt-1 text-xs text-rose-600">{fieldErrors.email}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-navy mb-1.5">
                          お電話番号（任意）
                        </label>
                        <input
                          type="tel"
                          value={phone}
                          onChange={(e) => {
                            setPhone(e.target.value);
                            if (fieldErrors.phone) {
                              setFieldErrors((prev) => ({ ...prev, phone: '' }));
                            }
                          }}
                          placeholder="03-0000-0000"
                          className={`w-full rounded-xl border bg-white px-4 py-3 text-sm text-navy focus:outline-none ${
                            fieldErrors.phone
                              ? 'border-rose-400 focus:border-rose-500 bg-rose-50/20'
                              : 'border-surface-border focus:border-spaq-cyan'
                          }`}
                        />
                        {fieldErrors.phone && (
                          <p className="mt-1 text-xs text-rose-600">{fieldErrors.phone}</p>
                        )}
                      </div>
                    </div>

                    {/* 対象施設・エリア（任意） */}
                    <div>
                      <label className="block text-xs font-bold text-navy mb-1.5">
                        対象施設の種別・所在地エリア（任意）
                      </label>
                      <input
                        type="text"
                        value={facilityInfo}
                        onChange={(e) => setFacilityInfo(e.target.value)}
                        placeholder="例：埼玉県内の冷蔵倉庫（高圧受電）、複数店舗への太陽光検討など"
                        className="w-full rounded-xl border border-surface-border bg-white px-4 py-3 text-sm text-navy focus:outline-none focus:border-spaq-cyan"
                      />
                    </div>

                    {/* お問い合わせ内容 */}
                    <div>
                      <label className="block text-xs font-bold text-navy mb-1.5">
                        ご相談・お問い合わせ内容 <span className="text-rose-500">※必須</span>
                      </label>
                      <textarea
                        rows={5}
                        required
                        value={message}
                        onChange={(e) => {
                          setMessage(e.target.value);
                          if (fieldErrors.message) {
                            setFieldErrors((prev) => ({ ...prev, message: '' }));
                          }
                        }}
                        placeholder="現在の課題感や、知りたいこと（概算試算、現地確認、デマコン・蓄電池連携、パートナー協業など）をご自由にご記入ください。"
                        className={`w-full rounded-xl border bg-white px-4 py-3 text-sm text-navy focus:outline-none ${
                          fieldErrors.message
                            ? 'border-rose-400 focus:border-rose-500 bg-rose-50/20'
                            : 'border-surface-border focus:border-spaq-cyan'
                        }`}
                      />
                      {fieldErrors.message && (
                        <p className="mt-1 text-xs text-rose-600">{fieldErrors.message}</p>
                      )}
                    </div>

                    {/* プライバシー同意 */}
                    <div className="space-y-1.5">
                      <label className="flex items-start gap-2.5 cursor-pointer text-xs text-navy leading-relaxed">
                        <input
                          type="checkbox"
                          checked={privacyAccepted}
                          onChange={(e) => {
                            setPrivacyAccepted(e.target.checked);
                            if (fieldErrors.privacyAccepted) {
                              setFieldErrors((prev) => ({ ...prev, privacyAccepted: '' }));
                            }
                          }}
                          className="mt-0.5 accent-spaq-cyan rounded"
                        />
                        <span>
                          <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="text-spaq-cyan-dark underline font-semibold mr-1">
                            プライバシーポリシー
                          </Link>
                          を確認し、個人情報の取り扱いに同意します。<span className="text-rose-500">※必須</span>
                        </span>
                      </label>
                      {fieldErrors.privacyAccepted && (
                        <p className="text-xs text-rose-600 pl-6">{fieldErrors.privacyAccepted}</p>
                      )}
                    </div>

                    {/* Cloudflare Turnstile */}
                    <div className="space-y-2 pt-2">
                      <div ref={turnstileContainerRef} id="turnstile-container" className="min-h-[65px]" />
                      {fieldErrors.turnstile && (
                        <p className="text-xs text-rose-600">{fieldErrors.turnstile}</p>
                      )}
                    </div>

                    {/* Error Message near Submit Button */}
                    {errorMessage && (
                      <div className="rounded-2xl border border-rose-300 bg-rose-50 p-4 text-rose-800 flex items-start gap-3 text-sm animate-fade-in">
                        <AlertCircle className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" />
                        <div>
                          <div className="font-bold">送信を完了できませんでした</div>
                          <div className="text-xs sm:text-sm mt-0.5">{errorMessage}</div>
                        </div>
                      </div>
                    )}

                    {/* Submit Button */}
                    <Button
                      type="submit"
                      variant="primary"
                      size="lg"
                      disabled={isSubmitting}
                      className="w-full justify-center disabled:opacity-60 disabled:cursor-not-allowed"
                      icon={
                        isSubmitting ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Send className="w-4 h-4" />
                        )
                      }
                    >
                      {isSubmitting ? '送信処理中...' : 'この内容で送信する'}
                    </Button>
                  </form>
                )}
              </div>

              {/* Right 4 cols: Phone & Info */}
              <div className="lg:col-span-4 space-y-6">
                <div className="rounded-3xl border border-surface-border bg-surface-sky p-6 sm:p-7 space-y-4">
                  <div className="text-xs font-bold text-spaq-cyan-dark uppercase tracking-wider">
                    PHONE & OFFICE
                  </div>
                  <h2 className="text-lg font-bold text-navy">
                    お電話でのお問い合わせ
                  </h2>
                  <p className="text-xs text-navy-muted leading-relaxed">
                    お急ぎのご相談や、フォームからの送信が難しい場合はお電話でも承っております。
                  </p>
                  <div className="bg-white rounded-2xl p-4 border border-surface-border">
                    <div className="flex items-center gap-2 text-xl font-bold text-navy font-mono-num">
                      <Phone className="w-5 h-5 text-spaq-cyan-dark" />
                      <span>{COMPANY_INFO.tel}</span>
                    </div>
                    <div className="text-xs text-navy-muted mt-1">
                      受付時間：{COMPANY_INFO.hours}
                    </div>
                  </div>
                  <div className="pt-2 border-t border-spaq-cyan/20 text-xs text-navy-muted space-y-1">
                    <div className="font-semibold text-navy">{COMPANY_INFO.name}</div>
                    <div className="flex items-start gap-1.5">
                      <MapPin className="w-4 h-4 text-spaq-cyan-dark shrink-0 mt-0.5" />
                      <span>{COMPANY_INFO.address}</span>
                    </div>
                  </div>
                </div>

                <div className="rounded-3xl border border-surface-border bg-white p-6 space-y-3 shadow-soft">
                  <h3 className="text-sm font-bold text-navy">
                    手元にデータがなくてもご相談いただけます
                  </h3>
                  <p className="text-xs text-navy-muted leading-relaxed">
                    「30分デマンドデータの取り寄せ方が分からない」「屋根の図面がすぐに見つからない」という段階でも問題ございません。取得方法のご案内や、初期ヒアリングから丁寧にサポートいたします。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
